import { useUnit } from 'effector-react';
import styled, { css } from 'styled-components';
import { $themeName, themeToggled } from '@/model/theme';
import { $view, navigated, type View } from '@/model/router';
import { cmdkToggled } from '@/model/cmdk';
import { $isAuthenticated, $user, logoutFx } from '@/model/auth';
import { Em } from '@/components/primitives';

const Bar = styled.nav`
  position: sticky;
  top: 0;
  z-index: 80;
  background: ${({ theme }) => theme.fx.glassNav};
  backdrop-filter: saturate(160%) blur(16px);
  -webkit-backdrop-filter: saturate(160%) blur(16px);
  border-bottom: 1px solid ${({ theme }) => theme.line.soft};
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[8]}`};
  max-width: ${({ theme }) => theme.widths.max};
  margin: 0 auto;

  @media (max-width: 600px) {
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[5]}`};
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

const Brand = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.hXs};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.snug};
  font-variation-settings: 'opsz' 60;
  color: ${({ theme }) => theme.ink[1]};
`;

const BrandMark = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 30% 26%, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0) 35%),
    radial-gradient(circle at 38% 32%, #c7d2fe 0%, #a5b4fc 50%, #818cf8 95%);
`;

const Center = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  background: ${({ theme }) => theme.surface.deep};
  padding: ${({ theme }) => theme.spacing[1]};
  border-radius: ${({ theme }) => theme.radii.pill};
  border: 1px solid ${({ theme }) => theme.line.line};

  @media (max-width: 720px) {
    display: none;
  }
`;

const TabBtn = styled.button<{ $on?: boolean }>`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.radii.pill};
  color: ${({ theme }) => theme.ink[3]};
  transition:
    color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    background ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  &:hover {
    color: ${({ theme }) => theme.ink[1]};
  }

  ${({ $on, theme }) =>
    $on &&
    css`
      background: ${theme.surface.elev};
      color: ${theme.ink[1]};
      box-shadow: ${theme.shadows.sh1};
    `}
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const IconBtn = styled.button<{ $pill?: boolean }>`
  width: ${({ $pill }) => ($pill ? 'auto' : '36px')};
  height: 36px;
  padding: ${({ $pill, theme }) => ($pill ? `0 ${theme.spacing[4]}` : 0)};
  border-radius: ${({ $pill, theme }) => ($pill ? theme.radii.pill : '50%')};
  border: 1px solid ${({ theme }) => theme.line.line};
  background: ${({ theme }) => theme.surface.elev};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.ink[2]};
  font-family: ${({ theme, $pill }) => ($pill ? theme.typography.fontFamilies.mono : 'inherit')};
  font-size: ${({ theme, $pill }) => ($pill ? theme.typography.fontSizes.monoSm : 'inherit')};
  letter-spacing: ${({ theme, $pill }) => ($pill ? theme.typography.letterSpacings.wide : 'normal')};
  transition:
    border-color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  &:hover {
    border-color: ${({ theme }) => theme.ink[3]};
    color: ${({ theme }) => theme.ink[1]};
  }

  @media (max-width: 480px) {
    ${({ $pill }) =>
      $pill &&
      `
      span:first-child { display: none; }
    `}
  }
`;

const Kbd = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoXs};
  color: ${({ theme }) => theme.ink[4]};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  background: ${({ theme }) => theme.surface.deep};
  border: 1px solid ${({ theme }) => theme.line.line};
  padding: 1px ${({ theme }) => theme.spacing[1]};
  border-radius: 4px;
`;

const SearchLabel = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
`;

const ThemeIcon = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
`;

const TABS: { id: View; label: string }[] = [
  { id: 'landing', label: 'обзор' },
  { id: 'onboard', label: 'начать' },
  { id: 'report', label: 'отчёт' },
];

export const TopNav = () => {
  const [view, themeName, isAuth, user] = useUnit([$view, $themeName, $isAuthenticated, $user]);

  return (
    <Bar>
      <Inner>
        <Brand type="button" onClick={() => navigated('landing')}>
          <BrandMark />
          lume<Em>.</Em>
        </Brand>
        <Center>
          {TABS.map((t) => (
            <TabBtn key={t.id} $on={view === t.id} onClick={() => navigated(t.id)}>
              {t.label}
            </TabBtn>
          ))}
        </Center>
        <Right>
          <IconBtn $pill type="button" onClick={() => cmdkToggled()} title="Поиск">
            <SearchLabel>поиск</SearchLabel>
            <Kbd>⌘K</Kbd>
          </IconBtn>
          {isAuth ? (
            <IconBtn
              $pill
              type="button"
              onClick={() => {
                void logoutFx();
                navigated('landing');
              }}
              title={user?.email ?? 'Выйти'}
            >
              <SearchLabel>выйти</SearchLabel>
            </IconBtn>
          ) : (
            <IconBtn $pill type="button" onClick={() => navigated('login')} title="Войти">
              <SearchLabel>войти</SearchLabel>
            </IconBtn>
          )}
          <IconBtn type="button" onClick={() => themeToggled()} title="Переключить тему">
            <ThemeIcon>{themeName === 'dark' ? '●' : '◐'}</ThemeIcon>
          </IconBtn>
        </Right>
      </Inner>
    </Bar>
  );
};
